import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { Status } from '../../common/constants';
import useNotification from '../notification/useNotification';
import { useSpinner } from '../ui/spinner/useSpinner';
import { isSuccessResponse } from '../../common/utils';
import {
  fetchPendingDeletions,
  forceDeleteDevice,
} from '../../pages/users/superAdmin/deviceManagement/pendingDeletionsSlice';
import {
  IPendingDeletion,
  IPendingDeletionsResponseState,
} from '../../common/types/devicesTypes';

export const usePendingDeletionsModal = () => {
  const dispatch: ThunkDispatch<IPendingDeletionsResponseState, undefined, Action> = useDispatch();
  const { pending, status, forceDeleteStatus } = useSelector(
    (state: IPendingDeletionsResponseState) => state.pendingDeletions
  );
  const { showSuccessNotification, showWarningNotification } = useNotification();

  const [showModal, setShowModal] = useState<boolean>(false);
  const [deviceToRemove, setDeviceToRemove] = useState<IPendingDeletion | null>(null);

  useEffect(() => {
    dispatch(fetchPendingDeletions());
  }, [dispatch]);

  const isBusy = (showModal && status === Status.PENDING) || forceDeleteStatus === Status.PENDING;
  useSpinner(isBusy ? Status.PENDING : Status.SUCCESS);

  const handleOpen = () => {
    setShowModal(true);
    dispatch(fetchPendingDeletions());
  };

  const handleClose = () => {
    setShowModal(false);
    setDeviceToRemove(null);
  };

  const askToRemove = (device: IPendingDeletion) => setDeviceToRemove(device);
  const cancelRemove = () => setDeviceToRemove(null);

  const confirmRemove = () => {
    if (!deviceToRemove) return;
    const { deviceId, displayName } = deviceToRemove;

    dispatch(forceDeleteDevice(deviceId)).then((response: any) => {
      if (isSuccessResponse(response.payload)) {
        showSuccessNotification(response.payload.message);
      } else {
        showWarningNotification(
          typeof response.payload === 'string'
            ? response.payload
            : `Could not remove ${displayName || deviceId}.`
        );
      }
      setDeviceToRemove(null);
    });
  };

  return {
    showModal,
    handleOpen,
    handleClose,
    pendingDevices: pending,
    isLoading: status === Status.PENDING,
    pendingCount: pending.length,
    deviceToRemove,
    askToRemove,
    cancelRemove,
    confirmRemove,
  };
};
